import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { AppointmentHistoryItem, callerIdService } from 'services/caller-id';
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon } from './icons';

const PAGE_SIZE = 5;

interface HistorySheetProps {
  customerId: number;
  customerName: string;
  // first page seed from customer-recent-appointments (0..2 items)
  recent: AppointmentHistoryItem[];
  rebookedId: number | null;
  onRebook: (item: AppointmentHistoryItem) => void;
  onClose: () => void;
}

// visit label: "07-01 · Gel Manicure, Pedicure" — startTime is pre-formatted
// 'hh:mm a MM-dd-yyyy'; only the MM-dd part is shown (no timezone re-parse)
const visitLabel = (item: AppointmentHistoryItem): string => {
  const datePart = item.startTime.split(' ').pop() ?? '';
  return `${datePart.slice(0, 5)} · ${item.serviceNames.join(', ')}`;
};

// Duration of a past visit in minutes, from its pre-formatted times.
export const visitDurationMin = (item: AppointmentHistoryItem): number => {
  const fmt = 'hh:mm a MM-DD-YYYY';
  const start = moment(item.startTime, fmt);
  const end = moment(item.endTime, fmt);
  const diff = end.diff(start, 'minute');
  return diff > 0 ? diff : 60;
};

// History bottom sheet within the booking page: recent visits first, "See more"
// switches to the paginated full history (GET customer-appointments).
const HistorySheet = ({ customerId, customerName, recent, rebookedId, onRebook, onClose }: HistorySheetProps) => {
  const [expanded, setExpanded] = useState(false);
  const [page, setPage] = useState(1);
  const [pageData, setPageData] = useState<AppointmentHistoryItem[] | null>(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!expanded) return;
    let cancelled = false;
    setLoading(true);
    callerIdService.getCustomerAppointments(customerId, page, PAGE_SIZE)
      .then(res => {
        if (cancelled) return;
        setPageData(res.data);
        setTotal(res.total);
      })
      .catch(() => {
        if (!cancelled) setPageData([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [expanded, page, customerId]);

  const rows = expanded ? pageData ?? [] : recent;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <div className="cid-form-scrim" onClick={onClose} />
      <div className="cid-history-sheet">
        <div className="cid-sheet-grab"><span /></div>
        <div className="cid-subsheet-header">
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="#1D4ED8" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" /><path d="M3 3v5h5M12 7v5l4 2" /></svg>
          <span className="cid-subsheet-title">History · {customerName}</span>
          <button type="button" className="cid-sheet-close" onClick={onClose}><CloseIcon size={17} /></button>
        </div>
        <div className="cid-history-rows">
          {rows.map(item => {
            const isRebooked = rebookedId === item.id;
            return (
              <div key={item.id} className="cid-history-row">
                <span className="label">{visitLabel(item)}</span>
                <button
                  type="button"
                  className={`cid-btn-rebook ${isRebooked ? 'applied' : ''}`}
                  onClick={() => onRebook(item)}
                >
                  {isRebooked ? '✓ Selected' : 'Re book'}
                </button>
              </div>
            );
          })}
          {loading && <div className="cid-history-empty">Loading…</div>}
          {!loading && rows.length === 0 && <div className="cid-history-empty">No history</div>}
        </div>
        <div className="cid-history-footer">
          {expanded ? (
            <>
              <button type="button" className="cid-date-nav" disabled={page <= 1} onClick={() => setPage(page - 1)}><ChevronLeftIcon size={15} /></button>
              <span className="cid-history-page">Page {page}/{totalPages}</span>
              <button type="button" className="cid-date-nav" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><ChevronRightIcon size={15} /></button>
            </>
          ) : (
            <button type="button" className="cid-history-more" onClick={() => setExpanded(true)}>See more</button>
          )}
        </div>
      </div>
    </>
  );
};

export default HistorySheet;
